import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Channel } from 'stream-chat'
import { useChatContext } from 'stream-chat-react'
import { useFloating, autoUpdate, flip, shift, offset } from '@floating-ui/react'
import { useUserInfo } from 'hooks/useUserInfo'
import { useGetBusinessProfile, IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import {
  useGetAdvisorDashboardQuery,
  useGetAdvisorsQuery,
  useGetBusinessesQuery,
  BusinessUserType,
} from '__generated__/api-types-and-hooks'
import { useTenantId } from 'utils/useTenantId'
import { USER_ROLES } from 'config'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { getInitials } from 'components/Chat/helper'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import {
  Camera,
  Check,
  Search,
  Trash2,
  ChevronLeft,
  X,
  MoreVertical,
  UserPlus,
  UserMinus,
} from 'lucide-react'
import UserGroupIcon from 'assets/svgs/user-group.svg'
import { uploadFiles } from 'components/AppFileUploader/AppFileUploader.helper'
import type { StreamChatGenerics } from '../../types'

type TabKey = 'info' | 'members'

interface GroupEditProps {
  channel: Channel<StreamChatGenerics>
  onClose: () => void
}

const GroupEdit: React.FC<GroupEditProps> = ({ channel, onClose }) => {
  // Tab and view state
  const [tab, setTab] = useState<TabKey>('members')
  const [showAddMembersView, setShowAddMembersView] = useState(false)

  // Member management state
  const [searchQuery, setSearchQuery] = useState('')
  const [addMembersSearchQuery, setAddMembersSearchQuery] = useState('')
  const [users, setUsers] = useState<IAssigneeOptions[]>([])
  const [isUpdating, setIsUpdating] = useState(false)
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<Set<string>>(new Set())
  const [memberToRemove, setMemberToRemove] = useState<IAssigneeOptions | null>(null)

  // Group info state
  const [groupIcon, setGroupIcon] = useState<string | null>((channel.data?.image as string) || null)
  const [groupName, setGroupName] = useState<string>((channel.data?.name as string) || '')
  const [groupDescription, setGroupDescription] = useState<string>(
    (channel.data?.description as string) || ''
  )
  const [openMemberMenu, setOpenMemberMenu] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const groupNameInputRef = useRef<HTMLInputElement>(null)
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null)

  // Floating UI for member action menu positioning
  const { refs, floatingStyles } = useFloating({
    open: !!openMemberMenu,
    onOpenChange: (open) => {
      if (!open) setOpenMemberMenu(null)
    },
    placement: 'bottom-end',
    strategy: 'fixed',
    middleware: [
      offset(4),
      flip({ fallbackPlacements: ['top-end', 'bottom-start', 'top-start'] }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const { client } = useChatContext()
  useUserInfo()
  const tenantId = useTenantId()
  const loggedInUser = useSelector((state: RootState) => state.user.user)
  const isBsoAdmin = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADMIN)
  const isBsoAdvisor = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADVISOR)

  // Get business profile to access team members (for Business Owners)
  const { assigneeOptions: businessAssigneeOptions } = useGetBusinessProfile({ enabled: true })

  // Get advisor dashboard data (for BSO_ADVISOR to see assigned clients)
  const { data: advisorData } = useGetAdvisorDashboardQuery({ tenantId })

  // BSO_ADMIN: Fetch all businesses (clients) in tenant
  const { data: allBusinessesData, isLoading: businessesLoading } = useGetBusinessesQuery(
    { getBusinessesInput: { tenantId, allStatuses: true } },
    { enabled: isBsoAdmin, refetchOnWindowFocus: false }
  )

  // BSO_ADMIN & BSO_ADVISOR: Fetch all advisors in tenant
  const { data: advisorsData, isLoading: advisorsLoading } = useGetAdvisorsQuery(
    { getAdvisorsInput: { tenantId } },
    { enabled: isBsoAdmin || isBsoAdvisor, refetchOnWindowFocus: false }
  )

  // Calculate if we're still loading users
  const isLoadingUsers = useMemo(() => {
    if (isBsoAdmin) {
      return businessesLoading || advisorsLoading
    } else if (isBsoAdvisor) {
      return advisorsLoading
    }
    return false
  }, [isBsoAdmin, isBsoAdvisor, businessesLoading, advisorsLoading])

  // Memoize assignee options to prevent infinite re-renders
  const assigneeOptions = useMemo(() => {
    let options: IAssigneeOptions[] = []

    if (isBsoAdmin) {
      // BSO_ADMIN: All businesses (clients) + all advisors in tenant
      const businessesResponse = JSON.parse(allBusinessesData?.getBusinesses?.data || '{}')
      const businessesList = businessesResponse?.list || []
      const businesses = businessesList
        .filter((b: any) => b.id && b.firstName && b.lastName)
        .map((b: any) => ({
          label: `${b.firstName} ${b.lastName}`,
          value: b.id,
          email: b.email,
          type: BusinessUserType.Member,
          user: {
            id: b.id,
            firstName: b.firstName,
            lastName: b.lastName,
            email: b.email,
          },
        }))

      const advisorsResponse = JSON.parse(advisorsData?.getAdvisors?.data || '{}')
      const advisorsList = advisorsResponse?.list || []
      const advisors = advisorsList
        .filter((a: any) => a.id && a.firstName && a.lastName)
        .map((a: any) => ({
          label: `${a.firstName} ${a.lastName}`,
          value: a.id,
          email: a.email,
          type: BusinessUserType.Member,
          user: {
            id: a.id,
            firstName: a.firstName,
            lastName: a.lastName,
            email: a.email,
          },
          isAdvisor: true,
        }))

      // Combine and deduplicate
      options = [...businesses, ...advisors]
      const seen = new Set()
      options = options.filter((opt) => {
        if (seen.has(opt.value)) return false
        seen.add(opt.value)
        return true
      })
    } else if (isBsoAdvisor) {
      // BSO_ADVISOR: Assigned clients + all advisors
      const clientsResponse = JSON.parse(advisorData?.getAdvisorDashboard?.data || '{}')
      const clients =
        clientsResponse?.accounts?.map((account: any) => ({
          label: `${account.firstName} ${account.lastName}`,
          value: account.id,
          email: account.email,
          type: BusinessUserType.Member,
          user: {
            id: account.id,
            firstName: account.firstName,
            lastName: account.lastName,
            email: account.email,
          },
        })) || []

      const advisorsResponse = JSON.parse(advisorsData?.getAdvisors?.data || '{}')
      const advisorsList = advisorsResponse?.list || []
      const advisors = advisorsList
        .filter((a: any) => a.id && a.firstName && a.lastName)
        .map((a: any) => ({
          label: `${a.firstName} ${a.lastName}`,
          value: a.id,
          email: a.email,
          type: BusinessUserType.Member,
          user: {
            id: a.id,
            firstName: a.firstName,
            lastName: a.lastName,
            email: a.email,
          },
          isAdvisor: true,
        }))

      // Combine and deduplicate
      options = [...clients, ...advisors]
      const seen = new Set()
      options = options.filter((opt) => {
        if (seen.has(opt.value)) return false
        seen.add(opt.value)
        return true
      })
    } else {
      // BUSINESS_OWNER: Team members + assigned advisors
      options = businessAssigneeOptions
    }

    // Filter out current user
    return options.filter((opt) => opt.value !== loggedInUser?.id)
  }, [
    isBsoAdmin,
    isBsoAdvisor,
    businessAssigneeOptions,
    allBusinessesData,
    advisorsData,
    advisorData,
    loggedInUser?.id,
  ])

  useEffect(() => {
    setUsers(assigneeOptions)
  }, [assigneeOptions])

  // Get current channel members (excluding current user)
  const currentMembers = useMemo(() => {
    const memberIds = Object.keys(channel.state.members || {}).filter((id) => id !== client.userID)
    return users.filter((user) => memberIds.includes(user.value))
  }, [channel.state.members, users, client.userID])

  // Get available users for adding (not currently members, filtered by search)
  const availableUsers = useMemo(() => {
    const memberIds = new Set(Object.keys(channel.state.members || {}))

    return users.filter(
      (user) =>
        !memberIds.has(user.value) &&
        user.label.toLowerCase().includes(addMembersSearchQuery.toLowerCase())
    )
  }, [users, channel.state.members, addMembersSearchQuery])

  // Close member menu when clicking outside
  useEffect(() => {
    if (!openMemberMenu) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const referenceElement = refs.reference.current
      const floatingElement = refs.floating.current

      if (
        floatingElement &&
        !floatingElement.contains(target) &&
        referenceElement &&
        referenceElement instanceof HTMLElement &&
        !referenceElement.contains(target)
      ) {
        setOpenMemberMenu(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openMemberMenu, refs])

  const handleIconClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const uploaded = await uploadFiles({
        files: [file],
        tenantId,
        clientId: '',
        userId: loggedInUser?.id,
      })
      setGroupIcon(uploaded?.[0]?.url || null)
    } catch (err) {
      console.error('Failed to upload group icon', err)
    }
  }

  const handleToggleUserSelection = (userId: string) => {
    const next = new Set(selectedUsersToAdd)
    if (next.has(userId)) {
      next.delete(userId)
    } else {
      next.add(userId)
    }
    setSelectedUsersToAdd(next)
  }

  const handleAddSelectedMembers = async () => {
    if (selectedUsersToAdd.size === 0) return

    setIsUpdating(true)
    try {
      await channel.addMembers(Array.from(selectedUsersToAdd))
      setSelectedUsersToAdd(new Set())
      setShowAddMembersView(false)
      setAddMembersSearchQuery('')
    } catch (error) {
      console.error('Failed to add members:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleRemoveMemberClick = (member: IAssigneeOptions) => {
    setMemberToRemove(member)
    setOpenMemberMenu(null)
  }

  const handleConfirmRemoveMember = async () => {
    if (!memberToRemove) return

    const userId = memberToRemove.value
    // Don't allow removing the current user
    if (userId === client.userID) {
      return
    }

    setIsUpdating(true)
    try {
      await channel.removeMembers([userId])
      setMemberToRemove(null)
    } catch (error) {
      console.error('Failed to remove member:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleSaveChanges = async () => {
    // Check if there are any changes to group info
    const hasGroupInfoChanges =
      groupName !== (channel.data?.name ?? '') ||
      groupDescription !== (channel.data?.description ?? '') ||
      groupIcon !== (channel.data?.image ?? null)

    if (!hasGroupInfoChanges) {
      onClose()
      return
    }

    setIsUpdating(true)
    try {
      // Use partial update so we don't overwrite other channel data (e.g. team, members).
      // channel.update() does a full replace and would remove any omitted fields.
      const set: Record<string, string | null> = {}
      const unset: string[] = []

      if (groupName !== (channel.data?.name ?? '')) {
        set.name = groupName.trim() || (channel.data?.name as string) || 'Group Chat'
      }
      if (groupDescription !== (channel.data?.description ?? '')) {
        if (groupDescription.trim()) {
          set.description = groupDescription.trim()
        } else {
          unset.push('description')
        }
      }
      if (groupIcon !== (channel.data?.image ?? null)) {
        if (groupIcon) {
          set.image = groupIcon
        } else {
          unset.push('image')
        }
      }

      const hasSet = Object.keys(set).length > 0
      const hasUnset = unset.length > 0
      if (hasSet || hasUnset) {
        await channel.updatePartial({
          ...(hasSet && { set }),
          ...(hasUnset && { unset: unset as any }),
        })
      }

      onClose()
    } catch (error) {
      console.error('Failed to update group:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const channelName = groupName || channel.data?.name || 'Group Chat'
  // Calculate total members
  const totalMembers = Object.keys(channel.state.members || {}).length

  const hasGroupInfoChanges =
    groupName !== (channel.data?.name ?? '') ||
    groupDescription !== (channel.data?.description ?? '') ||
    groupIcon !== (channel.data?.image ?? null)

  const modalTitle = (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <div className="grid h-10 w-10 place-items-center overflow-hidden rounded-full flex-shrink-0 bg-app-grey-30">
          {groupIcon ? (
            <img src={groupIcon} alt="" className="h-full w-full object-cover" aria-hidden />
          ) : (
            <img src={UserGroupIcon} alt="" className="h-5 w-5" aria-hidden />
          )}
        </div>
        <div className="text-lg font-semibold text-app-black-100">{channelName}</div>
      </div>

      {/* Tabs */}
      {!showAddMembersView && (
        <div className="flex items-center gap-6 border-b border-app-grey-25">
          <TabButton active={tab === 'info'} onClick={() => setTab('info')}>
            Info
          </TabButton>

          <TabButton active={tab === 'members'} onClick={() => setTab('members')}>
            <span className="inline-flex items-center gap-1.5">
              Members
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-app-primary-60 text-[11px] font-semibold leading-none text-app-white-100">
                {totalMembers}
              </span>
            </span>
          </TabButton>
        </div>
      )}
    </div>
  )

  return (
    <>
      <AppModalBox
        onClose={onClose}
        title={modalTitle}
        width="w-full min-w-[540px]"
        height="h-auto"
        rounded="2xl"
        backgroundColor="bg-app-white-100"
        childClass="px-5 pb-4 pt-4"
        showBorder={false}
        padding="px-5 pt-5 pb-0"
        position="center"
        closeOnBackdropClick={true}
      >
        {/* Info Tab */}
        {tab === 'info' && !showAddMembersView && (
          <div className="space-y-3">
            {/* Group icon */}
            <div className="rounded-2xl border border-app-grey-25 bg-app-white-100 p-4">
              <div className="text-sm font-medium text-app-black-100 mb-3">Group icon</div>

              <div className="flex items-start gap-4">
                <button
                  type="button"
                  onClick={handleIconClick}
                  className="relative grid h-16 w-16 place-items-center overflow-hidden rounded-2xl border border-app-grey-25 bg-app-grey-10 hover:bg-app-grey-20"
                >
                  {groupIcon ? (
                    <img src={groupIcon} alt="Group icon" className="h-full w-full object-cover" />
                  ) : (
                    <Camera className="h-6 w-6 text-app-grey-60" />
                  )}
                </button>

                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={handleIconClick}
                    className="inline-flex items-center justify-center rounded-xl bg-app-primary-60 px-4 py-2 text-sm font-semibold text-app-white-100 hover:bg-app-primary-70"
                  >
                    Upload picture
                  </button>
                  <button
                    type="button"
                    onClick={() => setGroupIcon(null)}
                    disabled={!groupIcon}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-app-grey-25 bg-app-white-100 px-4 py-2 text-sm font-semibold text-app-grey-80 hover:bg-app-grey-10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4 text-app-grey-60" />
                    Delete
                  </button>
                </div>
              </div>

              <div className="mt-3 text-xs text-app-grey-60">
                Click to update or upload a picture.
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />

            {/* Group name */}
            <div className="rounded-2xl border border-app-grey-25 bg-app-white-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-app-black-100">Group name</div>
                <button
                  type="button"
                  onClick={() => groupNameInputRef.current?.focus()}
                  className="text-sm font-medium text-app-primary-60 hover:text-app-primary-70"
                >
                  Edit
                </button>
              </div>
              <input
                ref={groupNameInputRef}
                type="text"
                placeholder="Group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="h-10 w-full rounded-xl border border-app-grey-25 bg-app-white-100 px-3 text-sm text-app-black-100 placeholder:text-app-grey-50 outline-none focus:border-app-primary-60 focus:ring-2 focus:ring-app-primary-10"
              />
            </div>

            {/* Description */}
            <div className="rounded-2xl border border-app-grey-25 bg-app-white-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-app-black-100">Description</div>
                <button
                  type="button"
                  onClick={() => descriptionInputRef.current?.focus()}
                  className="text-sm font-medium text-app-primary-60 hover:text-app-primary-70"
                >
                  Edit
                </button>
              </div>
              <textarea
                ref={descriptionInputRef}
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Optional: what is this group for?"
                rows={4}
                className="w-full resize-none rounded-xl border border-app-grey-25 bg-app-white-100 px-3 py-2 text-sm text-app-black-100 placeholder:text-app-grey-50 outline-none focus:border-app-primary-60 focus:ring-2 focus:ring-app-primary-10"
              />
            </div>
          </div>
        )}

        {/* Members Tab - Main View */}
        {tab === 'members' && !showAddMembersView && (
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-grey-50" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Find members"
                className="h-10 w-full rounded-xl border border-app-grey-25 bg-app-white-100 pl-10 pr-3 text-sm text-app-black-100 placeholder:text-app-grey-50 outline-none focus:border-app-primary-60 focus:ring-2 focus:ring-app-primary-10"
              />
            </div>

            {/* Add Member Button */}
            <button
              type="button"
              onClick={() => setShowAddMembersView(true)}
              className="inline-flex w-auto items-center gap-2 rounded-xl bg-app-primary-60 px-4 py-2.5 text-left text-sm font-semibold text-app-white-100 hover:bg-app-primary-70"
            >
              <UserPlus className="h-4 w-4 shrink-0 stroke-2 text-app-white-100 stroke-white" />
              Add member
            </button>

            {/* Current Members List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {/* Show current user first */}
              <div className="flex items-center gap-3 rounded-xl px-2 py-2">
                <AppAvatar
                  size="sm"
                  type="mixed"
                  shape="Rounded"
                  text={getInitials({
                    firstName: loggedInUser?.firstName || '',
                    lastName: loggedInUser?.lastName || '',
                  })}
                />
                <div className="flex-1 leading-tight">
                  <div className="text-sm font-medium text-app-black-100">
                    {loggedInUser?.firstName} {loggedInUser?.lastName} (You)
                  </div>
                  <div className="text-xs text-app-grey-60">Marketing - Admin</div>
                </div>
              </div>

              {/* Show other members */}
              {currentMembers
                .filter((member) => member.label.toLowerCase().includes(searchQuery.toLowerCase()))
                .map((member) => {
                  return (
                    <div
                      key={member.value}
                      className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-app-grey-10"
                    >
                      <AppAvatar
                        size="sm"
                        type="mixed"
                        shape="Rounded"
                        text={getInitials({
                          firstName: member.user?.firstName || '',
                          lastName: member.user?.lastName || '',
                        })}
                      />
                      <div className="flex-1 leading-tight">
                        <div className="text-sm font-medium text-app-black-100">{member.label}</div>
                        <div className="text-xs text-app-grey-60">
                          {member.user?.email?.includes('@')
                            ? member.user.email.split('@')[0]
                            : 'Operations'}{' '}
                          - Member
                        </div>
                      </div>
                      <div className="relative">
                        <button
                          ref={openMemberMenu === member.value ? refs.setReference : undefined}
                          type="button"
                          onClick={() =>
                            setOpenMemberMenu(openMemberMenu === member.value ? null : member.value)
                          }
                          className="p-2 hover:bg-app-grey-10 rounded-lg transition-colors duration-150"
                          disabled={isUpdating}
                        >
                          <MoreVertical className="h-4 w-4 text-app-grey-60" />
                        </button>
                      </div>
                    </div>
                  )
                })}

              {currentMembers.filter((member) =>
                member.label.toLowerCase().includes(searchQuery.toLowerCase())
              ).length === 0 && (
                <div className="py-8 text-center text-sm text-app-grey-60">
                  {searchQuery ? 'No members found' : 'No other members'}
                </div>
              )}
            </div>

            {/* Member action menu (positioned via Floating UI) */}
            {openMemberMenu &&
              (() => {
                const menuMember = currentMembers.find((m) => m.value === openMemberMenu)
                if (!menuMember) return null
                return (
                  <div
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="z-50 w-48 rounded-xl border border-app-grey-25 bg-app-white-100 shadow-lg overflow-hidden"
                  >
                    <div className="absolute top-0 right-10 w-0.5 h-2 bg-app-grey-30" />
                    <div className="border-t border-app-grey-25">
                      <button
                        type="button"
                        onClick={() => handleRemoveMemberClick(menuMember)}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-app-red-60 hover:bg-app-grey-10"
                      >
                        <UserMinus className="h-4 w-4" stroke="#E53E3E" strokeWidth={2} />
                        Remove User
                      </button>
                    </div>
                  </div>
                )
              })()}
          </div>
        )}

        {/* Members Tab - Add Members Slide View */}
        {tab === 'members' && showAddMembersView && (
          <div>
            <div className="rounded-2xl border border-app-grey-25 bg-app-white-100 p-4">
              {/* Back Button Header */}
              <button
                type="button"
                onClick={() => {
                  setShowAddMembersView(false)
                  setSelectedUsersToAdd(new Set())
                  setAddMembersSearchQuery('')
                }}
                className="flex items-center gap-2 mb-4 text-sm font-semibold text-app-primary-60 hover:text-app-primary-70"
              >
                <ChevronLeft className="h-4 w-4" />
                Add Members
              </button>

              <div className="text-xs text-app-grey-60 mb-3">
                Select people to add to this group
              </div>

              {/* Selected Users Section - max 4 per row */}
              {selectedUsersToAdd.size > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-app-grey-70 mb-2">Selected</div>
                  <div className="grid grid-cols-4 gap-2">
                    {Array.from(selectedUsersToAdd).map((userId) => {
                      const user = users.find((u) => u.value === userId)
                      if (!user) return null
                      return (
                        <div
                          key={userId}
                          className="inline-flex w-fit max-w-full justify-self-start items-center gap-1.5 rounded-lg bg-app-primary-20 px-3 py-1.5 text-xs font-medium text-app-primary-70"
                        >
                          <span className="max-w-[180px] truncate text-app-primary-70">
                            {user.label}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleToggleUserSelection(userId)}
                            className="flex-shrink-0 hover:bg-app-primary-30 rounded-lg p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Search */}
              <div className="relative mb-3">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-grey-50" />
                <input
                  value={addMembersSearchQuery}
                  onChange={(e) => setAddMembersSearchQuery(e.target.value)}
                  placeholder="Find members"
                  className="h-10 w-full rounded-xl border border-app-grey-25 bg-app-white-100 pl-10 pr-3 text-sm text-app-black-100 placeholder:text-app-grey-50 outline-none focus:border-app-primary-60 focus:ring-2 focus:ring-app-primary-10"
                />
              </div>

              {/* Available Users List */}
              <div className="max-h-96 overflow-y-auto">
                {isLoadingUsers ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-app-grey-30 border-t-app-primary-60"></div>
                      <div className="text-sm text-app-grey-60">Loading members...</div>
                    </div>
                  </div>
                ) : (
                  <>
                    {availableUsers.map((user) => {
                      const isSelected = selectedUsersToAdd.has(user.value)
                      return (
                        <button
                          type="button"
                          key={user.value}
                          onClick={() => handleToggleUserSelection(user.value)}
                          className="flex w-full items-center justify-between rounded-xl px-2 py-2 text-left hover:bg-app-grey-10"
                        >
                          <div className="flex items-center gap-3">
                            <AppAvatar
                              size="sm"
                              type="mixed"
                              shape="Rounded"
                              text={getInitials({
                                firstName: user.user?.firstName || '',
                                lastName: user.user?.lastName || '',
                              })}
                            />
                            <div className="leading-tight">
                              <div className="text-sm font-semibold text-app-black-100">
                                {user.label}
                              </div>
                              {user.email ? (
                                <div className="text-xs text-app-grey-60">{user.email}</div>
                              ) : (
                                <div className="text-xs text-app-grey-60">Member</div>
                              )}
                            </div>
                          </div>

                          {/* Checkbox */}
                          <div
                            className={[
                              'grid h-5 w-5 place-items-center rounded border',
                              isSelected
                                ? 'border-app-primary-60 bg-app-primary-60'
                                : 'border-app-grey-40 bg-app-white-100',
                            ].join(' ')}
                          >
                            {isSelected && (
                              <Check
                                className="h-3 w-3 !text-white"
                                strokeWidth={3}
                                stroke="white"
                              />
                            )}
                          </div>
                        </button>
                      )
                    })}

                    {availableUsers.length === 0 && (
                      <div className="py-8 text-center text-sm text-app-grey-60">
                        {addMembersSearchQuery ? 'No users found' : 'No members available'}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer with count */}
              <div className="mt-4 text-xs text-app-grey-60">
                {availableUsers.length} people available
              </div>
            </div>
          </div>
        )}

        {/* Created by and Leave Group (visible in all tabs when not in Add Members view) */}
        {!showAddMembersView && (
          <div className="space-y-4 mt-4">
            {/* Save Changes button for Info tab */}
            {tab === 'info' && hasGroupInfoChanges && (
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-app-grey-25 bg-app-white-100 px-4 py-2 text-sm font-semibold text-app-grey-80 hover:bg-app-grey-10"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveChanges}
                  disabled={isUpdating}
                  className="rounded-xl bg-app-primary-60 px-4 py-2 text-sm font-semibold text-app-white-100 hover:bg-app-primary-70 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}

            <div className="flex items-center justify-between rounded-2xl border border-app-grey-25 bg-app-white-100 p-4">
              <div className="text-sm text-app-grey-60">
                Created by {(channel.data?.created_by as any)?.name || 'Unknown'} on{' '}
                {channel.data?.created_at
                  ? new Date(channel.data.created_at as any).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })
                  : 'Unknown date'}
              </div>
              <button
                type="button"
                onClick={() => {
                  channel.removeMembers([client.userID!]).then(() => {
                    onClose()
                  })
                }}
                className="rounded-xl bg-app-red-60 px-4 py-2.5 text-sm font-semibold text-app-white-100 hover:bg-app-red-70 whitespace-nowrap"
              >
                Leave Group
              </button>
            </div>
          </div>
        )}

        {/* Footer - only when Add Members view is open */}
        {showAddMembersView && (
          <div className="flex items-center justify-between border-t border-app-grey-25 px-5 py-4">
            <button
              type="button"
              onClick={() => {
                setShowAddMembersView(false)
                setSelectedUsersToAdd(new Set())
                setAddMembersSearchQuery('')
              }}
              className="rounded-xl border border-app-grey-25 bg-app-white-100 px-4 py-2 text-sm font-semibold text-app-grey-80 hover:bg-app-grey-10"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleAddSelectedMembers}
              disabled={selectedUsersToAdd.size === 0 || isUpdating}
              className="rounded-xl bg-app-primary-60 px-4 py-2 text-sm font-semibold text-app-white-100 hover:bg-app-primary-70 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUpdating ? 'Adding...' : 'Add'}
            </button>
          </div>
        )}
      </AppModalBox>

      {/* Remove Member Confirmation Modal */}
      {memberToRemove && (
        <AppModalBox
          onClose={() => setMemberToRemove(null)}
          width="w-full max-w-md"
          height="h-auto"
          rounded="2xl"
          backgroundColor="bg-app-white-100"
          childClass="px-6 py-4"
          showBorder={false}
          padding="px-0 pt-0 pb-0"
          position="center"
          closeOnBackdropClick={true}
        >
          <div className="flex flex-col items-center text-center">
            <div className="mb-4">
              <AppAvatar
                size="md"
                type="mixed"
                shape="Rounded"
                text={getInitials({
                  firstName: memberToRemove.user?.firstName || '',
                  lastName: memberToRemove.user?.lastName || '',
                })}
              />
            </div>
            <h3 className="text-lg font-semibold text-app-black-100 mb-2">Remove Member?</h3>
            <p className="text-sm text-app-grey-60 mb-6">
              Are you sure you want to remove {memberToRemove.label} from this group?
            </p>
            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={() => setMemberToRemove(null)}
                className="flex-1 rounded-xl border border-app-grey-25 bg-app-white-100 px-4 py-2 text-sm font-semibold text-app-grey-80 hover:bg-app-grey-10"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRemoveMember}
                disabled={isUpdating}
                className="flex-1 rounded-xl bg-app-red-60 px-4 py-2 text-sm font-semibold text-app-white-100 hover:bg-app-red-70 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isUpdating ? 'Removing...' : 'Remove Member'}
              </button>
            </div>
          </div>
        </AppModalBox>
      )}
    </>
  )
}

export default GroupEdit

function TabButton({
  active,
  children,
  onClick,
}: {
  active?: boolean
  children: React.ReactNode
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'relative pb-3 text-sm font-semibold',
        active ? 'text-app-primary-60' : 'text-app-grey-60 hover:text-app-grey-80',
      ].join(' ')}
    >
      {children}
      <span
        className={[
          'absolute -bottom-[1px] left-0 h-[2px] w-full rounded-full transition',
          active ? 'bg-app-primary-60' : 'bg-transparent',
        ].join(' ')}
      />
    </button>
  )
}
